import moment from 'moment';

import { IMS_ACCESS_TOKEN_CACHE_KEY } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { logger } from '@/logger';
const IMS_EXPIRES_AT_CACHE_KEY = 'imsExpiresAt';
const IMS_ACCESS_TOKEN_PARAM_KEY = 'access_token';
const IMS_EXPIRES_IN_PARAM_KEY = 'expires_in';

// Cushion time before token expiration to trigger redirect to IMS auth (30 minutes)
const TOKEN_EXPIRY_CUSHION_MS = 30 * 60 * 1000;

export interface ImsProfile {
	email: string;
	userId: string;
	authId: string;
	name?: string;
	displayName?: string;
	first_name?: string;
	last_name?: string;
	countryCode?: string;
	emailVerified?: string;
	phoneNumber?: string | null;
}

export class ImsApi {
	private host: string = config.IMS_HOST;
	private profilePath: string = config.IMS_PROFILE_PATH;
	private authPath: string = config.IMS_AUTH_PATH;
	private logoutPath: string = config.IMS_LOGOUT_PATH;
	private clientId: string = config.IMS_CLIENT_ID;

	private cachedToken: string | null = null;
	private cachedTokenExpiresAt: string | null = null;
	private profile: ImsProfile | null = null;

	constructor() {
		const cachedAccessToken = localStorage.getItem(IMS_ACCESS_TOKEN_CACHE_KEY);
		const cachedExpiresAt = localStorage.getItem(IMS_EXPIRES_AT_CACHE_KEY);

		if (
			!cachedAccessToken ||
			!cachedExpiresAt ||
			moment(cachedExpiresAt).isBefore(moment())
		) {
			this.clearTokenCache();
			return;
		}

		this.cachedToken = cachedAccessToken;
		this.cachedTokenExpiresAt = cachedExpiresAt;
	}

	/**
	 * Check if the current token is valid and not expired (with cushion)
	 */
	public isCachedTokenValid = (): boolean => {
		const sessionDeadline = this.getSessionDeadline();
		if (!this.cachedToken || !sessionDeadline) {
			return false;
		}
		return moment().isBefore(sessionDeadline);
	};

	public getSessionDeadline = (): string | null => {
		if (!this.cachedTokenExpiresAt) {
			return null;
		}
		return moment(this.cachedTokenExpiresAt)
			.subtract(TOKEN_EXPIRY_CUSHION_MS, 'milliseconds')
			.toISOString();
	};

	/**
	 * Get the access token. If expired or missing, redirects to IMS auth to get a new token.
	 * Returns the token if valid, or null if redirect is triggered.
	 */
	public getCachedAccessTokenOrRedirect = (): string | null => {
		// If we have a valid token, return it immediately
		if (this.isCachedTokenValid()) {
			return this.cachedToken;
		}

		// Token is expired or missing - clear cache and redirect to get new token
		if (this.cachedToken) {
			logger.warn('IMS access token expired, redirecting to auth');
		}
		this.clearTokenCache();
		this.redirectToImsAuth();
		return null;
	};

	/**
	 * Return the cached access token, or null if there isn't one.
	 * Does not validate the token or trigger any redirect to auth.
	 */
	public getCachedAccessToken = (): string | null => {
		return this.cachedToken;
	};

	private clearTokenCache = () => {
		this.cachedToken = null;
		this.cachedTokenExpiresAt = null;
		this.profile = null;
		localStorage.removeItem(IMS_ACCESS_TOKEN_CACHE_KEY);
		localStorage.removeItem(IMS_EXPIRES_AT_CACHE_KEY);
	};

	public expireSession = () => {
		this.clearTokenCache();
	};

	public logout = () => {
		if (!this.clientId || !this.host || !this.logoutPath) {
			logger.error('Failed to log out of IMS', {
				clientId: this.clientId,
				host: this.host,
				logoutPath: this.logoutPath,
			});
			this.clearTokenCache();
			window.location.reload();
			return;
		}

		const params = new URLSearchParams({
			client_id: this.clientId,
			redirect_uri: window.location.origin,
		});

		this.clearTokenCache();
		window.location.href = `${this.host}${this.logoutPath}?${params.toString()}`;
	};

	public redirectToImsAuth = () => {
		if (!this.clientId || !this.host || !this.authPath) {
			logger.error('Failed to redirect to IMS auth', {
				clientId: this.clientId,
				host: this.host,
				authPath: this.authPath,
			});
			return;
		}

		const authUrl =
			`${this.host}${this.authPath}?` +
			`response_type=token&` +
			`scope=openid&` +
			`client_id=${this.clientId}&` +
			`client_redirect=${encodeURIComponent(this.getRedirectUrl())}&` +
			`redirect_uri=${encodeURIComponent(this.getRedirectUrl())}`;

		window.location.href = authUrl;
	};

	public getImsProfile = () => {
		return this.profile;
	};

	public fetchImsProfileOrRedirect = async (
		accessToken: string | null = null,
		redirectOnInvalid = true,
	): Promise<ImsProfile | null> => {
		const token = accessToken ?? this.getCachedAccessTokenOrRedirect();
		if (!token || !this.clientId || !this.host || !this.profilePath) {
			return null;
		}

		try {
			const response = await fetch(
				`${this.host}${this.profilePath}?client_id=${this.clientId}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				if (response.status === 401) {
					logger.warn('IMS profile fetch returned 401');
					this.clearTokenCache();
					if (redirectOnInvalid) {
						this.redirectToImsAuth();
					}
				} else {
					console.error('Failed to fetch IMS profile', response);
				}
				return null;
			}

			const profile = await response.json();
			this.profile = profile as ImsProfile;
			return this.profile;
		} catch {
			logger.warn('IMS profile fetch failed (likely CORS-blocked 401)');
			this.clearTokenCache();
			if (redirectOnInvalid) {
				this.redirectToImsAuth();
			}
			return null;
		}
	};

	public cacheTokenFromAuthCallbackUrl = () => {
		const urlParams = new URLSearchParams(window.location.hash.substring(1));
		const accessToken = urlParams.get(IMS_ACCESS_TOKEN_PARAM_KEY);
		const expires_in = urlParams.get(IMS_EXPIRES_IN_PARAM_KEY);

		if (accessToken && expires_in) {
			this.cachedToken = accessToken;
			this.cachedTokenExpiresAt = moment()
				.add(expires_in, 'seconds')
				.toISOString();
			localStorage.setItem(IMS_ACCESS_TOKEN_CACHE_KEY, accessToken);
			localStorage.setItem(IMS_EXPIRES_AT_CACHE_KEY, this.cachedTokenExpiresAt);

			window.history.replaceState({}, document.title, window.location.pathname);
		}
	};

	private getRedirectUrl = () => {
		return window.location.href;
	};
}
